import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChatContainer } from "../components/chat/ChatContainer";
import { SuggestedQuestionsOverlay } from "../components/chat/SuggestedQuestionsOverlay";
import { Header } from "../components/Header";
import { SessionGuard } from "../components/SessionGuard";
import { FullPageErrorState } from "../components/ui/FullPageErrorState";
import { useChatActions } from "../contexts/chat-actions-context";
import { useTodayChat } from "../hooks/useTodayChat";
import { useUser } from "../hooks/useUser";
import { useAnalytics } from "../lib/analytics";
import { api } from "../lib/api";
import { useClientTranslations } from "../lib/i18n/client";
import { type Chat, getUserChats } from "../services/chatService";
import styles from "./ChatPage.module.css";

/** The chat with the latest activity (last message, else creation time). */
function mostRecentChat(chats: Chat[]): Chat | null {
	const ts = (c: Chat) =>
		new Date(c.lastMessage?.createdAt ?? c.createdAt).getTime();
	let best: Chat | null = null;
	let bestTs = Number.NEGATIVE_INFINITY;
	for (const c of chats) {
		const t = ts(c);
		if (t > bestTs) {
			best = c;
			bestTs = t;
		}
	}
	return best;
}

interface ChatHistoryResponse {
	messages: Array<{
		id: string;
		role: string;
		content: string | Array<{ text?: string }>;
		createdAt: string;
	}>;
}

function ChatPageContent() {
	const { id } = useParams<{ id: string }>();
	// Chats belong to one account, and this cache can outlive an account switch
	// (a native re-auth keeps the same SPA session), so every chat cache key is
	// scoped by the Auth0 sub. Prefix-keyed invalidations still match.
	const { user: auth0User } = useAuth0();
	const userScope = auth0User?.sub ?? "anonymous";
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const analytics = useAnalytics();
	const queryClient = useQueryClient();
	const hasNavigatedToNewChat = useRef(false);
	const { t } = useClientTranslations("chat");
	const [isOverlayOpen, setIsOverlayOpen] = useState(false);
	const { triggerMessage } = useChatActions();

	const handleSuggestClick = useCallback(() => {
		setIsOverlayOpen(true);
	}, []);

	const handleOverlayClose = useCallback(() => {
		setIsOverlayOpen(false);
	}, []);

	const handleQuestionSelect = useCallback(
		(
			question: string,
			metadata?: { categoryName: string; categorySlug: string },
		) => {
			triggerMessage(
				question,
				metadata
					? {
							categoryName: metadata.categoryName,
							categorySlug: metadata.categorySlug,
						}
					: undefined,
			);
		},
		[triggerMessage],
	);

	// Check if user wants to force a new chat
	const forceNew = searchParams.get("new") === "true";

	// Fetch user data with details
	const {
		data: userData,
		error: userError,
		isLoading: userLoading,
	} = useUser();

	// On a plain /chat open (no id, not ?new=true), resume the user's most recent
	// conversation instead of starting an empty "today" chat (86ey1fqaa).
	// Mount time — used to require a chats fetch that completed AFTER this mount
	// before we redirect, so we never resume off a stale cached list (the
	// refetchOnMount "always" refetch can paint one frame with cached data first).
	const mountTimeRef = useRef(Date.now());
	const {
		data: recentChats,
		isLoading: recentChatsLoading,
		isFetching: recentChatsFetching,
		dataUpdatedAt: recentChatsUpdatedAt,
		error: recentChatsError,
	} = useQuery({
		queryKey: ["chats", userScope],
		queryFn: getUserChats,
		enabled: !id && !forceNew,
		// Always re-read on mount: a new chat created in a prior session marks
		// this list stale (ChatContainer onFinish), but invalidation alone won't
		// refetch a non-mounted query. Without this, returning to /chat resolves
		// the *stale* list → resumes the wrong chat until a second round-trip.
		refetchOnMount: "always",
		staleTime: 0,
	});

	const recentChatId = useMemo(
		() => (recentChats ? (mostRecentChat(recentChats)?.id ?? null) : null),
		[recentChats],
	);
	// The user has chats loaded but none exist → fall through to a fresh chat.
	const hasNoChats = !id && !forceNew && recentChats?.length === 0;

	// Redirect to the most recent chat once the list resolves. Wait for a FRESH
	// fetch (not a stale cached read) before navigating — otherwise the latch
	// below fires on stale data and locks us onto the wrong chat for the mount.
	const hasNavigatedToRecent = useRef(false);
	useEffect(() => {
		if (
			!id &&
			!forceNew &&
			recentChatId &&
			!recentChatsFetching &&
			// Only act on a list fetched AFTER this mount — never a stale cached read.
			recentChatsUpdatedAt > mountTimeRef.current &&
			!hasNavigatedToRecent.current
		) {
			hasNavigatedToRecent.current = true;
			navigate(`/chat/${recentChatId}`, { replace: true });
		}
	}, [
		id,
		forceNew,
		recentChatId,
		recentChatsFetching,
		recentChatsUpdatedAt,
		navigate,
	]);

	// Fetch today's chat only when forcing a new one or the user has no chats yet.
	const {
		data: todayChat,
		error: todayChatError,
		isLoading: todayChatLoading,
	} = useTodayChat({
		forceNew,
		enabled: !id && (forceNew || hasNoChats === true),
	});

	// Navigate to the new chat URL when we get a new chat from forceNew
	// This prevents the refetch issue caused by clearing params (which changes the cache key)
	useEffect(() => {
		if (forceNew && todayChat?.isNew && !hasNavigatedToNewChat.current) {
			hasNavigatedToNewChat.current = true;
			navigate(`/chat/${todayChat.chatId}`, { replace: true });
		}
	}, [forceNew, todayChat, navigate]);

	// If ID exists in URL, fetch that specific chat
	const {
		data: chatHistory,
		isLoading: chatHistoryLoading,
		isFetching: chatHistoryFetching,
		dataUpdatedAt: chatHistoryUpdatedAt,
		error: chatHistoryError,
	} = useQuery<ChatHistoryResponse>({
		queryKey: ["chat", id, userScope],
		queryFn: async () => {
			const response = await api.get(`/api/chat/${id}`);
			return response.data;
		},
		enabled: !!id,
		// A message just sent to this chat (in ChatContainer) is NOT written into
		// this cached history, so a stale read drops the newest message + reply when
		// returning to the chat. Always re-read on mount; the skeleton below holds
		// until that fresh fetch lands, so ChatContainer seeds useChat with the full
		// history (incl. the just-sent turn) instead of a stale snapshot.
		refetchOnMount: "always",
		staleTime: 0,
	});

	// Determine chat ID and messages
	const chatId = id || todayChat?.chatId || crypto.randomUUID();
	const initialMessages = id
		? chatHistory?.messages || []
		: todayChat?.messages || [];

	// Track page view
	useEffect(() => {
		analytics.logChat("page_viewed", {
			chat_id: chatId,
			is_historical: !!id,
			is_new_chat: todayChat?.isNew === true,
			message_count: initialMessages.length,
		});
	}, [analytics, chatId, id, todayChat?.isNew, initialMessages.length]);

	// Track user data errors
	useEffect(() => {
		if (userError) {
			const errorMessage =
				userError instanceof Error ? userError.message : "User data error";
			analytics.logError(errorMessage, { context: "chat_page_user_data" });
		}
	}, [userError, analytics]);

	// Error state: user data failed
	if (userError) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<FullPageErrorState
						titleKey="error.userDataTitle"
						descriptionKey="error.userDataDescription"
						onRetry={() =>
							queryClient.invalidateQueries({ queryKey: ["user"] })
						}
						t={t}
					/>
				</main>
			</div>
		);
	}

	// Loading state: user data or chat loading. Also hold while the recent-chats
	// list resolves and while we're about to redirect to the most recent chat
	// (id not set yet) — so we never flash an empty chat before the redirect.
	// Hold while the list loads AND while the forced refetch is in flight, so a
	// stale cached list never renders an empty/wrong chat before the redirect.
	const resolvingRecent =
		!id && !forceNew && (recentChatsLoading || recentChatsFetching);
	const redirectingToRecent = !id && !forceNew && !!recentChatId;
	// forceNew: a new chat was created and we're about to redirect to
	// /chat/:newId. Hold the skeleton so ChatContainer does NOT mount on the
	// transient /chat?new=true URL — otherwise it consumes a pending "Ask Eve"
	// message and sends it on a component that unmounts at the redirect, losing
	// the message (empty new chat). Mount only once on the stable /chat/:id.
	const redirectingToNew = !id && forceNew && todayChat?.isNew === true;
	// Hold until THIS chat's history has been re-fetched AFTER this mount, so
	// ChatContainer never seeds the conversation from a stale cached history that
	// omits a just-sent message+reply (regression: returning to a chat you just
	// messaged showed it without the newest turn). On a history fetch error we
	// stop holding so the page can render/recover instead of hanging on skeleton.
	const resolvingHistory =
		!!id &&
		!chatHistoryError &&
		(chatHistoryFetching || chatHistoryUpdatedAt <= mountTimeRef.current);
	if (
		userLoading ||
		!userData ||
		resolvingRecent ||
		redirectingToRecent ||
		redirectingToNew ||
		todayChatLoading ||
		chatHistoryLoading ||
		resolvingHistory
	) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<div className={styles.skeletonContainer} aria-busy="true">
						<div className={styles.skeletonBlock} />
						<div className={styles.skeletonBlock} />
						<div className={styles.skeletonBlock} />
					</div>
				</main>
			</div>
		);
	}

	// Error state: chat load failed
	const chatError = todayChatError || chatHistoryError || recentChatsError;
	if (chatError) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<FullPageErrorState
						titleKey="error.loadTitle"
						descriptionKey="error.loadDescription"
						onRetry={() =>
							queryClient.invalidateQueries({ queryKey: ["chat"] })
						}
						t={t}
					/>
				</main>
			</div>
		);
	}

	return (
		// `ph-mask-content` makes PostHog session replay mask all text and
		// nested input values inside the AI chat (config in src/lib/posthog.ts).
		// Provider chats and the rest of the app remain visible in replays.
		<div className={`${styles.wrapper} ph-mask-content`}>
			<Header />
			<main className={styles.main}>
				<ChatContainer
					id={chatId}
					user={userData}
					initialMessages={initialMessages}
					onSuggestClick={handleSuggestClick}
				/>
				<SuggestedQuestionsOverlay
					isOpen={isOverlayOpen}
					onClose={handleOverlayClose}
					onQuestionSelect={handleQuestionSelect}
					chatId={chatId}
				/>
			</main>
		</div>
	);
}

export function ChatPage() {
	return (
		<SessionGuard>
			<ChatPageContent />
		</SessionGuard>
	);
}
